import { useState, useEffect, useContext, useRef } from "react";
import { UserContext } from "../components/app-routes";
import { diaryApi } from "../services/api/diary";
import { useHistory, useParams } from "react-router-dom";
import Page from "../components/page";
import { Button, Box } from "@mui/material";
import { DiaryEntry } from "../models/diary-entry";
import DiaryForm, { DiaryFormValues } from "../components/diary-form";
import { ErrorContext } from "../components/app-routes";
import LoadingBackdrop from "../components/loading-backdrop";
import type { FormikHelpers } from "formik";
import { DateTime } from "luxon";
import StickyActionBar from "../components/sticky-action-bar";
import { getCustomLabel } from "../utils/custom-labels";

export default function AddDiaryPage() {
  const [busy, setBusy] = useState(false);
  const [initialValues, setInitialValues] = useState<DiaryFormValues>({
    subject: "",
    enteredBy: "",
    tags: "",
    body: "",
    archived: false,
  });
  const [wasSaved, setWasSaved] = useState(false);
  const [loading, setLoading] = useState(false);

  const history = useHistory();
  const { handleError } = useContext(ErrorContext);
  const { id } = useParams<{ id?: string }>();
  const isMounted = useRef(true);

  useEffect(() => {
    if (!id) return;

    setLoading(true);

    const fetchDiaryEntry = async () => {
      try {
        const note = await diaryApi.getSingle(parseInt(id));
        if (!note || !isMounted.current) return;

        setInitialValues({
          subject: note.subject,
          enteredBy: note.enteredBy,
          tags: note.tags,
          body: note.body,
          archived: note.archivedAt !== null,
          createdAt: note.createdAt
            ? DateTime.fromISO(note.createdAt)
            : undefined,
        });
      } catch (err) {
        if (isMounted.current) handleError(err);
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };

    fetchDiaryEntry();

    return () => {
      isMounted.current = false;
    };
  }, [id, handleError]);

  useEffect(() => {
    if (wasSaved) {
      history.replace("/home/diary");
    }
  }, [wasSaved, history]);

  const onSubmit = async (
    values: DiaryFormValues,
    _helpers: FormikHelpers<DiaryFormValues>,
  ) => {
    setBusy(true);
    try {
      const payload = {
        ...values,
        archivedAt: values.archived ? new Date().toISOString() : null,
        createdAt: values.createdAt?.toISO() ?? new Date().toISOString(),
      };

      if (id) {
        await diaryApi.update({ ...payload, id: parseInt(id) });
      } else {
        await diaryApi.create(payload);
      }

      setWasSaved(true);
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  };

  const context = useContext(UserContext);
  const grower = context.grower;

  return (
    <Page
      title={
        id
          ? `Edit ${getCustomLabel(grower ?? null, "diary")} Entry`
          : `Add ${getCustomLabel(grower ?? null, "diary")} Entry`
      }
    >
      {" "}
      {!loading && (
        <DiaryForm
          key={id ?? "new"}
          initialValues={initialValues}
          onSubmit={onSubmit}
          saved={wasSaved}
        >
          <StickyActionBar position="bottom">
            <Button
              variant="outlined"
              size="small"
              onClick={() => history.push("/home/diary")}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="small"
              disabled={busy}
            >
              {id ? "Save" : "Add"}
            </Button>
          </StickyActionBar>
        </DiaryForm>
      )}
      <LoadingBackdrop open={busy} />
    </Page>
  );
}
